import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, PenLine } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import type { ThemeHistory } from '../../hooks/useThemeHistory';
import type { VoiceState } from './types';

interface ThemeTextareaProps {
  themeHistory: ThemeHistory;
  voice: VoiceState;
  /** Account: the pre-flight content screen runs on blur. */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  /** A low box under the idea cards (grows with content). */
  compact?: boolean;
  placeholder?: string;
  testId?: string;
  /** Dark: the reader's closing slide. */
  tone?: 'light' | 'dark';
  maxLength?: number;
  /** Grow with the content (capped) so a prefilled idea is fully visible without scrolling. */
  autoGrow?: boolean;
  /** Cap for autoGrow in px (default 320); beyond it the box scrolls inside. The reader's closing slide
   *  uses 120 so the long episode idea cannot push the button below the slide's fold (2026-09-23). */
  autoGrowMax?: number;
  /** A small "tap to change" pill inside the box while it holds text and is not focused
   *  (slide, 2026-09-13: a prefilled sentence over a button read as a caption, not a field). */
  editHint?: string;
}

/** The free-text idea box with the press-and-hold microphone — the same box on every idea surface. */
export default function ThemeTextarea({
  themeHistory, voice, onBlur, autoFocus = false, compact = false, placeholder, testId = 'theme-input', tone = 'light', maxLength, autoGrow = false, autoGrowMax = 320, editHint,
}: ThemeTextareaProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [focused, setFocused] = useState(false);
  const hintVisible = !!editHint && !focused && themeHistory.value.trim() !== '';
  // A capped auto-grow box scrolls inside; the overlay pill would then sit on the last visible line,
  // so the hint moves to a caption under the box while the text overflows the cap (2026-09-23).
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!autoGrow || !el) return;
    el.style.height = 'auto';
    const capped = el.scrollHeight > autoGrowMax;
    el.style.height = `${Math.min(el.scrollHeight, autoGrowMax)}px`;
    setOverflowing(capped);
  }, [autoGrow, autoGrowMax, themeHistory.value]);
  const hintBelow = hintVisible && autoGrow && overflowing;
  const dark = tone === 'dark';
  const size = compact
    ? 'min-h-[5.5rem] md:min-h-[6.5rem] text-base leading-relaxed p-3 pr-12 md:p-4 md:pr-14 rounded-2xl border-2 resize-y'
    : 'min-h-[11rem] [@media(max-height:500px)]:min-h-[5.5rem] text-base leading-relaxed p-4 pr-12 sm:min-h-[12rem] md:min-h-[14rem] md:text-lg md:leading-normal md:p-5 md:pr-14 rounded-2xl border-2 resize-none';
  // Dark tone (the reader's closing slide): until 2026-09-13 the box was grey-on-grey with no
  // border contrast — parents took the prefilled idea for a caption. Now a white field with
  // dark ink and a visible border, i.e. the one thing on the slide that looks like an input.
  const skin = dark
    ? 'bg-white border-white/70 text-stone-900 placeholder:text-stone-400 shadow-inner focus:border-primary focus-visible:ring-0'
    : 'focus:border-primary';
  return (
    <div className="relative">
      <Textarea
        ref={ref}
        value={themeHistory.value}
        onChange={(e) => themeHistory.setUserValue(e.target.value)}
        onBlur={(e) => { setFocused(false); onBlur?.(e); }}
        onFocus={() => setFocused(true)}
        placeholder={placeholder ?? t('create.themePlaceholder')}
        data-testid={testId}
        rows={compact ? 2 : 3}
        autoFocus={autoFocus}
        maxLength={maxLength}
        className={`${size} ${skin} ${autoGrow ? 'overflow-y-auto resize-none' : ''} ${editHint && !hintBelow ? 'pb-8' : ''}`}
      />
      {hintVisible && !hintBelow && (
        <span
          className="absolute right-3 bottom-2 inline-flex items-center gap-1 text-[11px] text-stone-500 bg-white/90 px-1.5 py-0.5 rounded-full pointer-events-none"
          data-testid={`${testId}-edit-hint`}
          aria-hidden="true"
        >
          <PenLine className="w-3 h-3" /> {editHint}
        </span>
      )}
      {hintBelow && (
        <span
          className={`mt-1 flex items-center justify-end gap-1 text-[11px] ${dark ? 'text-white/60' : 'text-stone-500'} pointer-events-none`}
          data-testid={`${testId}-edit-hint`}
          aria-hidden="true"
        >
          <PenLine className="w-3 h-3" /> {editHint}
        </span>
      )}
      {voice.supported && (
        <button
          type="button"
          onPointerDown={(e) => { e.preventDefault(); voice.start(); }}
          onPointerUp={voice.stop}
          onPointerCancel={voice.stop}
          onPointerLeave={(e) => { if (e.buttons) voice.stop(); }}
          onContextMenu={(e) => e.preventDefault()}
          className={`absolute right-3 top-3 w-10 h-10 rounded-full flex items-center justify-center transition-all touch-none select-none ${
            voice.listening
              ? 'bg-destructive text-white animate-pulse'
              : 'bg-stone-100 text-stone-500 hover:bg-primary/10 hover:text-primary'
          }`}
          title={voice.listening ? t('create.listening') : t('create.voiceInput')}
          aria-label={t('create.voiceInput')}
        >
          {voice.listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
      )}
    </div>
  );
}
